import { useEffect, useState } from 'react';
import type { StoreCode } from '@familyhub/shared';
import { api, ApiError, isOffline } from './api/client';
import { clearLocalData, db, setMeta } from './db';
import { runModuleCleanups } from './platform/cleanup';

/**
 * The session (API §2).
 *
 * The cookie is HttpOnly, so this code cannot read it and does not try. "Signed
 * in" means `GET /me` succeeded at least once; the user id is cached in
 * IndexedDB so a phone that opens the app with no signal is not thrown back to
 * a login screen it cannot complete. Sessions are long-lived on purpose — a
 * household app that logs you out in a supermarket is broken.
 */

export interface Me {
  user: { id: string; email: string; display_name: string };
  household: { id: string; name: string };
  members: { id: string; display_name: string }[];
}

export type SessionState =
  | { status: 'loading' }
  | { status: 'in'; me: Me | null; userId: string }
  | { status: 'out' };

export async function login(email: string, password: string): Promise<Me> {
  const me = await api<Me>('/api/v1/auth/login', { method: 'POST', body: { email, password } });
  await setMeta('user_id', me.user.id);
  await setMeta('me', me);
  return me;
}

export async function logout(): Promise<void> {
  try {
    await api('/api/v1/auth/logout', { method: 'POST' });
  } finally {
    // Local data goes even if the server call failed: a shared phone must not
    // keep the household's list after someone signs out. Every module's own
    // store is wiped too, through the cleanup registry.
    await clearLocalData();
    await runModuleCleanups();
  }
}

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const cachedId = (await db.meta.get('user_id'))?.value as string | undefined;
      const cachedMe = (await db.meta.get('me'))?.value as Me | undefined;

      try {
        const me = await api<Me>('/api/v1/me');
        await setMeta('user_id', me.user.id);
        await setMeta('me', me);
        if (!cancelled) setState({ status: 'in', me, userId: me.user.id });
      } catch (err) {
        if (isOffline(err) && cachedId) {
          // Offline with a session we have used before: carry on. The list is
          // local, and that is the whole point of it being local.
          if (!cancelled) setState({ status: 'in', me: cachedMe ?? null, userId: cachedId });
          return;
        }
        if (err instanceof ApiError && err.isUnauthenticated) {
          await clearLocalData();
          await runModuleCleanups();
        }
        if (!cancelled) setState({ status: 'out' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Display names for the store codes. Values stay English snake_case (API §1). */
export const STORE_LABELS: Record<StoreCode, string> = {
  ah: 'Albert Heijn',
  jumbo: 'Jumbo',
  lidl: 'Lidl',
  ekoplaza: 'Ekoplaza',
  other: 'Overal',
};
